import { useEffect } from 'react'
import type { UseQuestionnaireSelectionConfig } from './types'

/**
 * Hook for questionnaire selection using Strategy Pattern
 *
 * Executes guards first, then runs strategies in order until one returns shouldContinue: false.
 * Each strategy can decide whether to stop the chain or continue to the next one.
 *
 * @param config Configuration including context, strategies, guards, and callback
 *
 * @example
 * ```typescript
 * useQuestionnaireSelection({
 *   context: {
 *     defaultFormResults,
 *     monarchQuestionnaireName,
 *     currentStepIndex,
 *     monarchIsLoading,
 *     monarchError: err,
 *     questionSet
 *   },
 *   strategies: [
 *     new UrlQuestionnaireStrategy(),
 *     new DefaultFormResultsStrategy(),
 *     new MonarchStrategy(),
 *     new FallbackStrategy()
 *   ],
 *   guards: [
 *     questionnaireAlreadySetGuard,
 *     userAlreadyStartedGuard
 *   ],
 *   onSelectionChange: setMonarchQuestionnaireName
 * })
 * ```
 */
export function useQuestionnaireSelection(config: UseQuestionnaireSelectionConfig): void {
  const { context, strategies, guards, onSelectionChange } = config

  useEffect(() => {
    // Step 1: Check guards
    for (const guard of guards) {
      const result = guard(context)
      if (result.shouldBlock) {
        return
      }
    }

    // Step 2: Execute strategies in order
    for (const strategy of strategies) {
      if (!strategy.isEnabled()) {
        continue
      }

      const result = strategy.execute(context)

      if (result.questionnaire !== null) {
        onSelectionChange(result.questionnaire)
        return
      }

      if (!result.shouldContinue) {
        return
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    context.defaultFormResults.questionnaire,
    context.monarchQuestionnaireName,
    context.currentStepIndex,
    context.monarchIsLoading,
    context.monarchError,
    context.questionSet,
    onSelectionChange,
  ])
}
